"use client";

import * as React from "react";
import { Box } from "../../components/ui/box";
import { cn } from "../../lib/utils";
import { Icon, type IconName } from "../../components/ui/icon";
import { Text } from "../../components/ui/text";
import { colorVar, type Color } from "../../lib/colors";
import type { SurfaceToken } from "../../components/ui/box-classes";
import {
  AVATAR_SIZE_PX,
  EntityAvatar,
  EntityAvatarFallback,
  EntityAvatarImage,
  getInitials,
  type EntityAvatarPresence,
  type EntityAvatarSize,
} from "./entity-avatar";
import type { Omittable } from "../../lib/props";

/**
 * Stacked avatar group with overlapping circles and a "+N" overflow chip.
 *
 * The separation rings and the icon-tile tint bake are both painted in the
 * color of the surface the group sits on, so the circles read as cutouts of
 * it. That surface is the `surface` prop: `background` — the page — covers tables,
 * lists and screens, and a group inside a dialog or menu passes `popover`.
 */

export type EntityGroupItem = Omittable<{
  id: string;
  name?: string | null;
  imageUrl?: string | null;
  /** When set, the slot renders a tinted icon tile instead of an avatar. */
  icon?: IconName | null;
  /** Color name from the shared scale. Only used when `icon` is set. */
  iconColor?: Color | null;
  /** Suspended treatment — greyscale + dimmed, matching EntityAvatar. */
  muted?: boolean | null;
  /** Invited treatment — dashed ring + reduced opacity, matching EntityAvatar. */
  invited?: boolean | null;
  /** Live presence indicator on the avatar's corner (avatar slots only) —
   *  ignored for icon tiles and the count chip. */
  presence?: EntityAvatarPresence | null;
}>;

export type EntityGroupSize = "xs" | "sm" | "default";

export type EntityGroupProps = Omittable<{
  items: EntityGroupItem[];
  /** Maximum number of stacked slots to display — "stack" variant only
   *  ("expanded" always renders the first item plus a count chip). When item
   *  count exceeds this, the last slot becomes a circular count chip. Default: 3 */
  max?: number;
  /** Slot size. Default: "sm" */
  size?: EntityGroupSize;
  /**
   * Layout variant.
   * - "stack" (default): overlapping circles with a circular count chip tail.
   * - "expanded": first item's visual + name in a pill, with a circular count
   *   chip overlapping the pill's right edge for the rest.
   */
  variant?: "stack" | "expanded";
  /** The surface the group is mounted on. Drives the separation rings and the
   *  icon-tile tint bake. Default: "background". */
  surface?: SurfaceToken;
}>;

// Box must match the avatar px size so the clipping wrapper never crops it.
const BOX_SIZE: Record<EntityGroupSize, number> = {
  xs: AVATAR_SIZE_PX.xs,
  sm: AVATAR_SIZE_PX.sm,
  default: AVATAR_SIZE_PX.default,
};
const AVATAR_SIZE: Record<EntityGroupSize, EntityAvatarSize> = {
  xs: "xs",
  sm: "sm",
  default: "default",
};
const ICON_SIZE = { xs: "xs", sm: "sm", default: "md" } as const;
const ROUNDED = { xs: "sm", sm: "sm", default: "md" } as const;
const FALLBACK_TEXT_SIZE = { xs: "2xs", sm: "xs", default: "xs" } as const;

/**
 * Ring effect around overlapping circles. A box-shadow, not a border, so the
 * ring sits outside the element and does not shrink the content area. It is
 * painted in the surface's own fill, so the circles read as cutouts of it.
 * Written out per surface — Tailwind only emits classes it can see whole.
 */
const RING_CLASS: Record<SurfaceToken, string> = {
  sunken: "shadow-[0_0_0_1.5px_var(--sunken)]",
  background: "shadow-[0_0_0_1.5px_var(--background)]",
  card: "shadow-[0_0_0_1.5px_var(--card)]",
  popover: "shadow-[0_0_0_1.5px_var(--popover)]",
  sidebar: "shadow-[0_0_0_1.5px_var(--sidebar)]",
  elevated: "shadow-[0_0_0_1.5px_var(--elevated)]",
};

/** An entity's color as a paintable `var()`, falling back to the muted foreground when
 *  the entity has none. Callers narrow a stored string with `toColor` first. */
export function resolveEntityColor(color?: Color | null): string {
  return color ? colorVar(color) : "var(--muted-foreground)";
}

/** Bake the 15%-tint appearance into a solid paint by mixing the color over
 *  `surface`. Solid, so stacked chips never show through. */
export function bakeEntityTint(
  color?: Color | null,
  surface: SurfaceToken = "background",
): string {
  return `color-mix(in oklab, ${resolveEntityColor(color)} 15%, var(--${surface}))`;
}

export type EntityIconTileProps = Omittable<{
  icon: IconName;
  /** Color name from the shared scale. */
  color?: Color | null;
  /** Icon glyph size. Default "sm". */
  size?: React.ComponentProps<typeof Icon>["size"];
  /** Tile edge length in px. */
  boxSize: number;
  /** Tile corner rounding. Default "sm". */
  rounded?: "sm" | "md";
  /** The surface the tile is mounted on — the tint is baked over it so the fill
   *  stays opaque. Default: "background". */
  surface?: SurfaceToken;
}>;

/** The canonical tinted icon tile for icon-kind entities (teams, workflows,
 *  issue types) — the glyph in its color over that color baked at 15% onto
 *  the ambient surface. One component so avatar stacks, chips, and selector
 *  rows can never drift apart. Color is runtime data, hence inline style. */
export function EntityIconTile({
  icon,
  color,
  size = "sm",
  boxSize,
  rounded = "sm",
  surface = "background",
}: EntityIconTileProps): React.ReactElement {
  return (
    <Box
      width={boxSize}
      height={boxSize}
      rounded={rounded}
      overflow="hidden"
      direction="row"
      align="center"
      justify="center"
      shrink={false}
      style={{ backgroundColor: bakeEntityTint(color, surface) }}
    >
      <Icon name={icon} size={size} color={color ?? "muted"} />
    </Box>
  );
}

/** Overflow display caps at "9+" — beyond nine the precise count stops being scannable. */
function formatOverflow(count: number): string {
  return count > 9 ? "9+" : `+${count}`;
}

export function EntityGroup({
  items,
  max = 3,
  size = "sm",
  variant = "stack",
  surface = "background",
}: EntityGroupProps): React.ReactElement {
  // Destructured rather than length-tested: the leading slot is what the
  // `expanded` variant renders from, and this is the one check that proves it
  // is there.
  const [first] = items;
  if (first === undefined) {
    return <Icon name="user-plus" size="md" color="muted" />;
  }

  const boxSize = BOX_SIZE[size];
  const avatarSize = AVATAR_SIZE[size];
  const iconSize = ICON_SIZE[size];
  const rounded = ROUNDED[size];
  const fallbackTextSize = FALLBACK_TEXT_SIZE[size];

  // User items render the circular avatar treatment; icon items (teams,
  // stages, ...) keep their padded icon in a tinted rounded square.
  const isIconSlot = (item: EntityGroupItem) => !!item.icon && !item.imageUrl;

  const renderSlot = (item: EntityGroupItem) => {
    if (isIconSlot(item)) {
      return (
        <EntityIconTile
          icon={item.icon!}
          color={item.iconColor}
          size={iconSize}
          boxSize={boxSize}
          rounded={rounded}
          surface={surface}
        />
      );
    }
    return (
      <EntityAvatar
        size={avatarSize}
        muted={!!item.muted}
        invited={!!item.invited}
        alt={item.name ?? "Avatar"}
        presence={item.presence}
      >
        {item.imageUrl ? (
          <EntityAvatarImage src={item.imageUrl} alt={item.name ?? "Avatar"} />
        ) : null}
        <EntityAvatarFallback>
          <Text size={fallbackTextSize} weight="medium">
            {item.name ? getInitials(item.name) : "?"}
          </Text>
        </EntityAvatarFallback>
      </EntityAvatar>
    );
  };

  const renderCountChip = (count: number) => (
    <EntityAvatar size={avatarSize} alt={`${count} more`}>
      <EntityAvatarFallback>
        <Text size={fallbackTextSize} weight="medium">
          {formatOverflow(count)}
        </Text>
      </EntityAvatarFallback>
    </EntityAvatar>
  );

  const ringClass = RING_CLASS[surface];

  if (variant === "expanded") {
    const overflow = items.length - 1;
    const firstIsIcon = isIconSlot(first);
    const firstIsGray = !!first.muted || !!first.invited;
    // The capsule is a darker shade of the leading slot's own color — the
    // avatar fallback's primary for users, the icon tint for icon kinds — so
    // the pill reads as one tinted unit, not avatar-on-gray.
    const pillColor = firstIsGray
      ? "var(--muted)"
      : bakeEntityTint(firstIsIcon ? first.iconColor : "primary");
    return (
      <Box direction="row" align="center" shrink={false}>
        <Box
          direction="row"
          align="center"
          gap="xs"
          rounded={firstIsIcon ? rounded : "full"}
          overflow="hidden"
          shrink={false}
          paddingRight={first.name ? "sm" : undefined}
          style={{ backgroundColor: pillColor }}
        >
          {renderSlot(first)}
          {first.name ? (
            <Text size="sm" weight="medium" truncate>
              {first.name}
            </Text>
          ) : null}
        </Box>
        {overflow > 0 ? (
          <Box marginLeftNegative="xs"
            width={boxSize}
            height={boxSize}
            rounded="full"
            overflow="hidden"
            shrink={false}
            className={cn(ringClass)}
          >
            {renderCountChip(overflow)}
          </Box>
        ) : null}
      </Box>
    );
  }

  const hasOverflow = items.length > max;
  const visible = hasOverflow ? items.slice(0, max - 1) : items;
  const overflowCount = hasOverflow ? items.length - (max - 1) : 0;
  const step = boxSize - 6;

  // An empty group returned above, and an overflowing one always keeps the
  // count chip, so there is always at least one slot.
  const totalSlots = visible.length + (hasOverflow ? 1 : 0);
  const containerWidth = boxSize + step * (totalSlots - 1);

  return (
    <Box
      position="relative"
      width={containerWidth}
      height={boxSize}
      shrink={false}
      data-slot="entity-group"
    >
      {visible.map((item, index) => (
        <Box
          key={`${item.id}-${index}`}
          position="absolute"
          top={0}
          left={index * step}
          width={boxSize}
          height={boxSize}
          rounded={isIconSlot(item) ? rounded : "full"}
          overflow="hidden"
          direction="row"
          align="center"
          justify="center"
          className={ringClass}
        >
          {renderSlot(item)}
        </Box>
      ))}
      {hasOverflow ? (
        <Box
          position="absolute"
          top={0}
          left={visible.length * step}
          width={boxSize}
          height={boxSize}
          rounded="full"
          overflow="hidden"
          direction="row"
          align="center"
          justify="center"
          className={ringClass}
        >
          {renderCountChip(overflowCount)}
        </Box>
      ) : null}
    </Box>
  );
}
